import { useMemo, useRef, useEffect } from "react";
import { PageHeader } from "@layouts/PageHeader";
import { StepProgress, StepItem } from "@components/StepProgress";
import { useAppDispatch, useAppSelector } from "@hooks/redux";
import {
  setCurrentStep,
  selectCurrentStep,
  updateReady,
  resetToInit,
  selectVideoCreatingParams,
  fetchJobDetail,
  selectVideoGenerating,
  selectJobDetailLoading,
} from "./videoCreatingSlice";
import { BasicInfo, type BasicInfoRef } from "./BasicInfo";
import { ProductInfo, type ProductInfoRef } from "./ProductInfo";
import AutoDescription from "./AutoDescription";
import { useVideoCreatingReady } from "./hooks";
import { useNavigationGuard } from "@layouts/NavigationGuard";
import { useQueryParams } from "@hooks/useQueryParams";
import { useNavigate } from "react-router-dom";
import { TestId } from "@shared/testIds";
import report from "@report";
import { shouldReportVideoCreatingFromKalodataProduct } from "@utils/kalodataReferral";

const steps: StepItem[] = [
  { key: "basic", title: "Product Setup" },
  { key: "product", title: "Video Settings" },
  { key: "description", title: "Script Review" },
];

export const PageVideoCreating = () => {
  useVideoCreatingReady(updateReady, resetToInit);
  const dispatch = useAppDispatch();
  const currentStep = useAppSelector(selectCurrentStep);
  const params: any = useAppSelector(selectVideoCreatingParams);
  const videoGenerating = useAppSelector(selectVideoGenerating);
  const basicInfoRef = useRef<BasicInfoRef>(null);
  const productInfoRef = useRef<ProductInfoRef>(null);
  const { noGuardFn, GuardModal } = useNavigationGuard(true);
  const [jobId] = useQueryParams(["jobId"]);
  useAppSelector(selectJobDetailLoading);
  const navigate = useNavigate();

  // 如果 URL 携带 jobId，从接口初始化各步数据
  useEffect(() => {
    if (jobId) {
      dispatch(fetchJobDetail(jobId));
    }
  }, [jobId]);

  // 计算禁用的步骤
  const disabledSteps = useMemo(() => {
    const disabled: number[] = [];
    const { basicInfo, productInfo } = params;

    // 第一步：商品名称为空时，禁止跳转到第二步和第三步
    if (!basicInfo.productTitle?.trim()) {
      disabled.push(1, 2);
    }
    // 第二步：类目为空时，禁止跳转到第三步
    else if (!productInfo.category?.trim()) {
      disabled.push(2);
    }

    return disabled;
  }, [params.basicInfo.productTitle, params.productInfo.category]);

  const handleStepChange = (step: number) => {
    // 向前切换且只跨一步时，先执行当前步的「下一步」逻辑（校验、上传、保存），再由 onNext 切步
    if (step === currentStep + 1) {
      if (currentStep === 0) {
        basicInfoRef.current?.triggerNext();
        return;
      }
      if (currentStep === 1) {
        productInfoRef.current?.triggerNext();
        return;
      }
    }
    dispatch(setCurrentStep(step));
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      dispatch(setCurrentStep(currentStep + 1));
    }
  };

  useEffect(() => {
    const isReport = shouldReportVideoCreatingFromKalodataProduct();
    console.log("isReport video_creating_from_kalodata_product", isReport);
    if (!isReport) {
      return;
    }
    report.event("video_creating_from_kalodata_product", {
      kalo_pathname: "/video-creating",
      kalo_json: { kalo_referrer: document.referrer },
    });
  }, []);

  return (
    <div className="VideoCreating h-screen relative" data-testid={TestId.VideoCreatingFlow}>
      <PageHeader
        title="AI Video Creator"
        disabledBack={videoGenerating}
        onBack={() => {
          dispatch(resetToInit());
          navigate("/video");
        }}
      />
      <div className="w-[720px] mx-auto h-[calc(100vh-53px)] flex flex-col">
        <StepProgress
          className="py-[20px] border-x border-kds-border px-[16px]"
          steps={steps}
          current={currentStep}
          onChange={handleStepChange}
          disabledSteps={disabledSteps}
          disabledBack={videoGenerating}
        />

        {/* Step1, Basic Info */}
        {currentStep === 0 && (
          <BasicInfo
            ref={basicInfoRef}
            onNext={handleNext}
            onParseProductFailed={() => {
              noGuardFn();
              navigate("/video");
            }}
          />
        )}
        {/* Step2, Product Info */}
        {currentStep === 1 && <ProductInfo ref={productInfoRef} onNext={handleNext} />}
        {/* Step3, Auto Description */}
        {currentStep === 2 && <AutoDescription onGenerateSuccess={noGuardFn} />}
      </div>

      {GuardModal}
    </div>
  );
};
